/**
 * Lab all-cheaters ui-select — single player from searchHierarchyNew.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Field, Input, Spinner } from '@w7/ui'
import { searchHierarchyNew } from '@/api/map/member'

const DEBOUNCE_MS = 100
const SUGGEST_LIMIT = 20

export type CheaterPlayerPickerProps = {
  value: string | null
  onChange: (username: string | null) => void
}

export function CheaterPlayerPicker({ value, onChange }: CheaterPlayerPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const prevValueRef = useRef(value)
  const [draft, setDraft] = useState(value ?? '')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [box, setBox] = useState<{ top: number; left: number; width: number } | null>(null)

  // Mirror a confirmed pick. Clear draft only when parent drops a real selection
  // (e.g. after Add) and the user is not mid-search — typing calls onChange(null).
  useEffect(() => {
    if (value != null) {
      setDraft(value)
    } else if (!open && prevValueRef.current != null) {
      setDraft('')
    }
    prevValueRef.current = value
  }, [value, open])

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft])

  // Portalled list escapes modal overflow — anchor it to the input wrap.
  useLayoutEffect(() => {
    if (!open) {
      setBox(null)
      return
    }
    const place = () => {
      const a = wrapRef.current?.getBoundingClientRect()
      if (!a) return
      setBox({ top: a.bottom + 4, left: a.left, width: a.width })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const t = event.target as Node
      if (rootRef.current?.contains(t) || listRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const membersQ = useQuery({
    queryKey: ['cm', 'security', 'cheater-player-search', debounced],
    queryFn: async () => {
      const rows = await searchHierarchyNew({ filter: debounced.trim(), page: 1 })
      return rows.map((r) => r.username).filter(Boolean)
    },
    enabled: open,
    staleTime: 10_000,
    retry: false,
  })

  const suggestions = (membersQ.data ?? []).slice(0, SUGGEST_LIMIT)

  const pick = (name: string) => {
    onChange(name)
    setDraft(name)
    setOpen(false)
  }

  const rect = wrapRef.current?.getBoundingClientRect()
  const placed = box ?? {
    top: (rect?.bottom ?? 0) + 4,
    left: rect?.left ?? 0,
    width: rect?.width ?? 0,
  }

  const list =
    open && typeof document !== 'undefined'
      ? createPortal(
          <ul
            ref={listRef}
            id="cm-cheater-player-suggest"
            className="cm-cheater-player-suggest cm-cheater-player-suggest--portal"
            role="listbox"
            style={{ top: placed.top, left: placed.left, width: placed.width }}
          >
            {membersQ.isFetching && suggestions.length === 0 ? (
              <li className="cm-cheater-player-empty" role="presentation">
                <Spinner size={14} /> Searching…
              </li>
            ) : null}
            {suggestions.length === 0 && !membersQ.isFetching ? (
              <li className="cm-cheater-player-empty">No players found</li>
            ) : (
              suggestions.map((name) => (
                <li key={name}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={value === name}
                    className="cm-cheater-player-option"
                    onMouseDown={(e) => {
                      // Keep input focus (Lab ui-select) — click must not blur first.
                      e.preventDefault()
                      pick(name)
                    }}
                  >
                    {name}
                  </button>
                </li>
              ))
            )}
          </ul>,
          document.body,
        )
      : null

  return (
    <div className="cm-cheater-player-picker" ref={rootRef}>
      <Field label="Select player" required>
        {({ id, describedBy }) => (
          <div className="cm-cheater-player-wrap" ref={wrapRef}>
            {/* Stable Input — no suffix Spinner (that remount killed the caret). */}
            <Input
              id={id}
              aria-describedby={describedBy}
              placeholder="Select player"
              value={draft}
              aria-expanded={open}
              aria-controls="cm-cheater-player-suggest"
              aria-autocomplete="list"
              aria-busy={membersQ.isFetching}
              onChange={(e) => {
                setDraft(e.target.value)
                onChange(null)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
            />
          </div>
        )}
      </Field>
      {list}
    </div>
  )
}
